import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Link,
  RadioGroup,
  Select,
  SelectItem,
} from '@heroui/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { FormFieldSecure } from '@/components/layout/Elements';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import useSegment from '@/hooks/useSegment';
import {
  useSetupMutation,
  useSignupMutation,
} from '@/patient-app-common/api/authApi';
import { isEmail } from '@/patient-app-common/utils';
import { useAppSelector } from '@/redux/hooks';

import { ConsentDoc } from './ConsentDoc';

const SUBMIT_FORM_EVENT = 'Signup final button clicked';
const SIGNUP_ERROR = 'Signup failed';

export function AccountForm({ jsonForm, token }) {
  const { t } = useTranslation('login');
  const { pathname } = useLocation();
  const { sendTrackEvent } = useSegment();
  const { requirePasswordLength13 } = useHospitalFlags();

  let action;

  if (_.startsWith(pathname, '/setup')) {
    action = 'setup';
  }

  if (_.startsWith(pathname, '/signup')) {
    action = 'signup';
  }

  const [signup, { isLoading: isLoadingSignup }] = useSignupMutation();
  const [setup, { isLoading: isLoadingSetup }] = useSetupMutation();
  const isLoading = isLoadingSignup || isLoadingSetup;

  const idempotencyKey = useRef(crypto.randomUUID());
  const submittingRef = useRef(false);

  const { token: patientToken } = useAppSelector((state) => state.auth);

  const [consentForm, setConsentForm] = useState([]);
  const [userConsent, setUserConsent] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [form, setForm] = useState(_.cloneDeep(jsonForm));
  const [serverError, setServerError] = useState('');

  const [fieldErrors, setFieldErrors] = useState({});

  const [termsCheckbox, setTermsCheckbox] = useState(false);

  useEffect(() => {
    if (!form.length) return;

    const formAfterConsent = [...form];
    const extractedConsentFields = _.remove(
      formAfterConsent,
      (input) => input.fieldCode.indexOf('consent') > -1
    );

    if (extractedConsentFields.length) {
      setUserConsent(false);
      setConsentForm(extractedConsentFields);
      setForm(formAfterConsent);
    }
  }, []);

  const handleFormChange = (fieldCode, value) => {
    setFieldErrors({ ...fieldErrors, [fieldCode]: '' });
    const consentFormCopy = [...consentForm];
    const formCopy = [...form];

    consentFormCopy.forEach((field) => {
      if (field.fieldCode === fieldCode) {
        field.value = value;
      }
    });
    setConsentForm(consentFormCopy);

    formCopy.forEach((field) => {
      if (field.fieldCode === fieldCode) {
        field.value = value;
      }
    });

    setForm(formCopy);
  };

  const handlePasswordBlur = () => {
    const { inputErrors } = validatePassword();

    if (confirmPassword === '') {
      inputErrors.confirm_password = '';
    }
    setFieldErrors({ ...fieldErrors, ...inputErrors });
  };

  const handleConfirmPasswordBlur = () => {
    const { inputErrors } = validatePassword();
    setFieldErrors({ ...fieldErrors, ...inputErrors });
  };

  const handleDateChange = (date, fieldCode) => {
    if (!date) return;

    let formattedDate = dayjs(date.toString()).format('MM/DD/YYYY');

    if (!dayjs(formattedDate, 'MM/DD/YYYY', true).isValid()) {
      formattedDate = '';
    }

    handleFormChange(fieldCode, formattedDate);
  };

  const validateConsent = (consentCheckbox) => {
    const result = {
      valid: true,
      inputError: '',
    };

    if (consentCheckbox.required && consentCheckbox.value !== 'true') {
      result.inputError = t('accountform.error.consent_required');
      result.valid = false;
    } else if (!consentCheckbox.value) {
      result.inputError = t('accountform.error.consent_blank');
      result.valid = false;
    }

    return result;
  };

  const handleConfirmConsent = (e) => {
    e.preventDefault();

    const validated = validateForm(consentForm);
    if (!validated) return;
    setUserConsent(true);
  };

  const validateUser = (value, fieldCode) => {
    const result = {
      valid: true,
      inputError: '',
    };

    if (fieldCode === 'username' && !/^[a-zA-Z0-9]*$/.test(value)) {
      result.inputError = `${t('accountform.error.invalid_user')}\n`;
      result.valid = false;
    }

    if (fieldCode === 'email' && !isEmail(value)) {
      result.inputError = `${t('common.error.invalid_email')}\n`;
      result.valid = false;
    }
    return result;
  };

  const validatePassword = () => {
    const result = {
      valid: true,
      inputErrors: { password: '', confirm_password: '' },
    };

    const hasNumber = /[0-9]/.test(password);
    const requiredLength = requirePasswordLength13 ? 13 : 8;
    const lengthError = `common.error.invalid_password_${requiredLength}`;

    if (password && (password.length < requiredLength || !hasNumber)) {
      result.inputErrors['password'] = `${t(lengthError)}`;
      result.valid = false;
    }

    if (password && confirmPassword === '') {
      result.inputErrors['confirm_password'] =
        `${t('accountform.error.blank_input', { input: { fieldName: t('common.input.confirm_password') } })}`;
      result.valid = false;
    } else if (password !== confirmPassword) {
      result.inputErrors['confirm_password'] =
        `${t('common.error.confirm_password')}`;
      result.valid = false;
    }

    return result;
  };

  const validateFieldFormat = (field) => {
    const result = {
      valid: true,
      inputError: '',
    };

    if (!_.has(field, 'fieldFormat') || !field.value) return result;

    const regex = new RegExp(field.fieldFormat);
    result.valid = regex.test(field.value);

    if (!result.valid) {
      result.inputError = `Invalid format for ${field.fieldName} \n`;
    }

    return result;
  };

  const validateForm = (formFields) => {
    let validated = true;
    let missing = {};

    const { valid: validPassword, inputErrors: passwordErrors } =
      validatePassword();

    if (!validPassword) {
      validated = false;
      missing = { ...missing, ...passwordErrors };
    }

    formFields.forEach((input) => {
      const consentCheck =
        input.fieldCode === 'consent' && input.fieldType === 'checkbox';
      const isBlank = input.required && input.visible && !input.value;

      const { valid: validFormat, inputError: errorFormat } =
        validateFieldFormat(input);

      if (!validFormat) {
        validated = false;
        missing[input.fieldCode] = errorFormat;
      }

      if (input.fieldCode === 'email' || input.fieldCode === 'username') {
        const { valid: validUser, inputError: errorUser } = validateUser(
          input.value,
          input.fieldCode
        );

        if (!validUser) {
          missing[input.fieldCode] = errorUser;
          validated = false;
        }
      }

      if (consentCheck) {
        const { valid: validConsent, inputError: errorConsent } =
          validateConsent(input);

        if (!validConsent) {
          missing[input.fieldCode] = errorConsent;
          validated = false;
        }
      } else if (isBlank) {
        missing[input.fieldCode] =
          `${t('accountform.error.blank_input', { input })}`;
        validated = false;
      }
    });

    setFieldErrors({ ...missing });

    return validated;
  };

  const dispatchSetup = async () => {
    try {
      const result = await setup({
        token,
        fields: form.concat(consentForm),
        idempotencyKey: idempotencyKey.current,
      }).unwrap();

      if (result.error) {
        submittingRef.current = false;
        idempotencyKey.current = crypto.randomUUID();
        handleValidationError(result.error);
      }
    } catch (error) {
      submittingRef.current = false;
      idempotencyKey.current = crypto.randomUUID();
      setServerError(`${t('common.error.server')}: ${JSON.stringify(error)}`);
    }
  };

  const dispatchSignup = async () => {
    try {
      const result = await signup({
        token,
        fields: form.concat(consentForm),
        idempotencyKey: idempotencyKey.current,
      }).unwrap();

      if (result.error) {
        submittingRef.current = false;
        idempotencyKey.current = crypto.randomUUID();
        handleValidationError(result.error);
        sendTrackEvent(SIGNUP_ERROR, { ...result, token });
      }
    } catch (error) {
      submittingRef.current = false;
      idempotencyKey.current = crypto.randomUUID();
      setServerError(t('common.error.server'));

      if (_.isObject(error)) {
        setServerError(`${t('common.error.server')}: ${JSON.stringify(error)}`);
        sendTrackEvent(SIGNUP_ERROR, { ...error, token });
      }
    }
  };

  const handleValidationError = (resultError) => {
    switch (resultError) {
      case 'duplicate_email':
      case 'duplicate_user':
      case 'duplicate_request':
        setServerError(t('accountform.error.duplicate_user'));
        break;

      case 'info_verification_failed':
        setServerError(t('accountform.error.info_verification'));
        break;

      case 'info_blank':
        setServerError(t('accountform.error.info_blank'));
        break;

      default:
        setServerError(`${t('common.error.server')}: ${resultError}`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setServerError('');

    const validated = validateForm(form);
    if (!validated) return;

    sendTrackEvent(SUBMIT_FORM_EVENT);

    if (!termsCheckbox) {
      setServerError(t('accountform.error.terms'));
      return;
    }

    submittingRef.current = true;

    if (action === 'signup') {
      dispatchSignup();
    }

    if (action === 'setup') {
      dispatchSetup();
    }
  };

  if (patientToken) {
    return <Navigate to="/" replace />;
  }

  if (consentForm.length && !userConsent) {
    return (
      <div className="flex flex-col items-center my-12">
        <div className="sm:max-w-xl w-full">
          <Card shadow="sm" className="border border-grey px-4">
            <CardHeader>
              <h1 className="text-xl font-semibold">
                {t('accountform.title')}
              </h1>
            </CardHeader>
            <CardBody className="gap-4">
              <Form onSubmit={handleConfirmConsent}>
                {consentForm
                  .filter((field) => field.visible === true)
                  .map((field) => (
                    <>
                      <FormElement
                        key={field.fieldName}
                        field={field}
                        fieldErrors={fieldErrors}
                        handleFormChange={handleFormChange}
                        handleDateChange={handleDateChange}
                        setConfirmPassword={setConfirmPassword}
                        setPassword={setPassword}
                        handlePasswordBlur={handlePasswordBlur}
                        handleConfirmPasswordBlur={handleConfirmPasswordBlur}
                        setFieldErrors={setFieldErrors}
                      />
                    </>
                  ))}
                <Button
                  className="mt-4 mx-auto w-1/2"
                  color="primary"
                  type="submit"
                >
                  {t('accountform.button.next')}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center my-12">
      <div className="sm:max-w-xl w-full">
        <Card shadow="sm" className="border border-grey px-4">
          <CardHeader>
            <h1 className="text-xl font-semibold">{t('accountform.title')}</h1>
          </CardHeader>
          <CardBody className="gap-4">
            <Form onSubmit={handleSubmit} validationBehavior="aria">
              {form
                .filter((field) => field.visible === true)
                .map((field) => (
                  <>
                    <FormElement
                      key={field.fieldName}
                      field={field}
                      fieldErrors={fieldErrors}
                      handleFormChange={handleFormChange}
                      handleDateChange={handleDateChange}
                      setConfirmPassword={setConfirmPassword}
                      setPassword={setPassword}
                      handlePasswordBlur={handlePasswordBlur}
                      handleConfirmPasswordBlur={handleConfirmPasswordBlur}
                      setFieldErrors={setFieldErrors}
                    />
                  </>
                ))}

              <div className="flex flex-col items-center place-self-center">
                <hr className="my-4 w-full border-small border-default-200 border-opacity-1 border-gray-300" />
                <p className="text-sm">{t('accountform.text.review_terms')}</p>
                <Link
                  className="mt-2 text-sm"
                  href="https://seamless.md/eula/"
                  isExternal
                >
                  {t('common.terms_of_use')}
                </Link>
                <Link
                  className="mb-2 text-sm"
                  href="https://seamless.md/privacy/"
                  isExternal
                >
                  {t('common.privacy_policy')}
                </Link>
              </div>
              <div className="flex flex-col items-start w-full flex-wrap max-w-full max-h-screen">
                <Checkbox
                  isSelected={termsCheckbox}
                  onValueChange={setTermsCheckbox}
                  className="max-w-full w-full"
                >
                  <span className="text-sm">{t('accountform.checkbox')}</span>
                </Checkbox>
              </div>

              <Button
                className="mt-4 mx-auto w-1/2"
                color="primary"
                isDisabled={isLoading}
                isLoading={isLoading}
                type="submit"
              >
                {t('accountform.button.submit')}
              </Button>
            </Form>
            <Alert
              color="danger"
              hideIcon
              isVisible={!_.isEmpty(serverError)}
              radius="none"
              title={serverError}
            />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function FormElement({
  field,
  fieldErrors,
  handleFormChange,
  handleDateChange,
  setConfirmPassword,
  setPassword,
  handlePasswordBlur,
  handleConfirmPasswordBlur,
  setFieldErrors,
}) {
  const { t } = useTranslation('login');
  const error = fieldErrors[field.fieldCode];
  let inputType = 'text';

  switch (field.fieldType) {
    case 'email':
    case 'phone':
      inputType = field.fieldType;
    // eslint-disable-next-line no-fallthrough
    case 'string':
      return (
        <Input
          autoComplete="off"
          className="mb-4"
          color="primary"
          description={field.hint}
          errorMessage={error}
          isInvalid={!_.isEmpty(error)}
          isRequired={field.required}
          label={field.fieldName}
          onChange={(e) => handleFormChange(field.fieldCode, e.target.value)}
          type={inputType}
          validationBehavior="aria"
          variant="bordered"
        />
      );

    case 'password':
      return (
        <>
          <FormFieldSecure
            className="mb-4"
            color="primary"
            description={field.hint}
            errorMessage={error}
            isInvalid={!_.isEmpty(error)}
            isRequired
            label={t('common.input.password')}
            onChange={(e) => {
              setPassword(e.target.value);
              handleFormChange(field.fieldCode, e.target.value);
            }}
            onBlur={handlePasswordBlur}
            validationBehavior="aria"
            variant="bordered"
          />
          <FormFieldSecure
            className="mb-4"
            color="primary"
            isInvalid={
              !_.isEmpty(fieldErrors['confirm_password']) ||
              !_.isEmpty(fieldErrors['password'])
            }
            isRequired
            errorMessage={fieldErrors['confirm_password']}
            label={t('common.input.confirm_password')}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              handleFormChange(field.fieldCode, e.target.value);
              if (fieldErrors['confirm_password']) {
                setFieldErrors({
                  ...fieldErrors,
                  confirm_password: '',
                });
              }
            }}
            onBlur={handleConfirmPasswordBlur}
            variant="bordered"
          />
        </>
      );

    case 'date':
      return (
        <DatePicker
          className="mb-4"
          color="primary"
          description={field.hint}
          errorMessage={error}
          isInvalid={!_.isEmpty(error)}
          isRequired={field.required}
          label={field.fieldName}
          onChange={(date) => handleDateChange(date, field.fieldCode)}
          showMonthAndYearPickers
          validationBehavior="aria"
          variant="bordered"
        />
      );

    case 'dropdown':
      return (
        <Select
          className="mb-4"
          color="primary"
          description={field.hint}
          errorMessage={error}
          isInvalid={!_.isEmpty(error)}
          isRequired={field.required}
          label={field.fieldName}
          isMultiline={true}
          onChange={(e) => handleFormChange(field.fieldCode, e.target.value)}
          variant="bordered"
        >
          {field.options.map((option) => (
            <SelectItem
              classNames={{
                title: 'whitespace-normal break-words',
              }}
              key={option[1]}
            >
              {option[0]}
            </SelectItem>
          ))}
        </Select>
      );

    case 'checkbox':
      return (
        <div className="flex flex-col items-start w-full flex-wrap max-w-full max-h-screen">
          <RadioGroup
            description={field.hint}
            errorMessage={error}
            isInvalid={!_.isEmpty(error)}
          >
            {field.options.map((option) => (
              <Checkbox
                key={_.toString(option.checkboxType)}
                isSelected={option.checkboxType === field.value}
                onChange={() => {
                  const checkedVal =
                    field.value === option.checkboxType
                      ? ''
                      : option.checkboxType;
                  handleFormChange(field.fieldCode, checkedVal);
                }}
                value={option.checkboxType}
                required={field.required}
                className="max-w-full w-full"
              >
                <span className="text-sm break-words max-w-full w-full whitespace-normal">
                  {option.checkboxText}
                  {field.required && (
                    <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                  )}
                </span>
              </Checkbox>
            ))}
          </RadioGroup>
        </div>
      );

    case 'consent_form':
      return <ConsentDoc template={field.value} />;

    default:
      return <div />;
  }
}
